
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CountryAirport {
  id: number;
  name: string;
  location: string;
  iata_code: string | null;
  reviews: {
    average_score: number;
    total_reviews: number;
    hand_check_success_rate: number;
  };
}

export function useCountryAirports(country: string) {
  return useQuery<CountryAirport[]>({
    queryKey: ['airports', 'country', country],
    queryFn: async () => {
      if (!country) return [];
      
      console.log(`Looking for airports in country: ${country}`);
      
      const { data: allAirports, error } = await supabase
        .from('airports')
        .select('id, name, location, iata_code');
      
      if (error) throw error;
      
      // Filter airports by country
      const filteredAirports = allAirports.filter(airport => {
        const airportLocation = airport.location.toLowerCase();
        return airportLocation.includes(country.toLowerCase());
      });
      
      console.log(`Found ${filteredAirports.length} airports in ${country}`);
      
      // If no airports found, return empty array
      if (filteredAirports.length === 0) return [];
      
      // Get all reviews for these airports
      const airportNames = filteredAirports.map(airport => airport.name);
      
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('airport_name, film_friendly_score, hand_check_success')
        .in('airport_name', airportNames);
        
      if (reviewsError) throw reviewsError;
      
      // Calculate statistics for each airport
      return filteredAirports.map(airport => {
        const airportReviews = reviews?.filter(r => r.airport_name === airport.name) || [];
        const totalReviews = airportReviews.length;
        
        let averageScore = 0;
        let handCheckSuccessRate = 0;
        
        if (totalReviews > 0) {
          averageScore = airportReviews.reduce((sum, r) => sum + r.film_friendly_score, 0) / totalReviews;
          const successfulHandChecks = airportReviews.filter(r => r.hand_check_success).length;
          handCheckSuccessRate = (successfulHandChecks / totalReviews) * 100;
        }
        
        return {
          ...airport,
          reviews: {
            average_score: averageScore,
            total_reviews: totalReviews,
            hand_check_success_rate: handCheckSuccessRate
          }
        };
      });
    },
    enabled: !!country,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
